import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { CheckLg, Plus, XLg } from 'react-bootstrap-icons';

import Framework from '../../components/framework';
import GroupLink from '../../components/framework/group_link';
import PageHeader from '../../components/framework/pages/page-header';
import ReadMoreLink from '../../components/framework/read-more-link';
import SectionHeader from '../../components/framework/sections/section-header';
import PriceItem from '../../components/shop/price_item';
import MarkerThumb from '../../components/test/marker-thumb';
import Trans from '../../components/translation/trans';
import useGroup from '../../hooks/use_group';
import useMarker from '../../hooks/use_marker';
import useProduct from '../../hooks/use_product';
import useQueryParameters from '../../hooks/use_query_parameters';
import useSiteMetadata from '../../hooks/use_site_metadata';
import useTranslation from '../../hooks/use_translation';
import useWindowSize from '../../hooks/use_window_resize';
import { getGroupsByMarker } from '../../utils/helpers';

const getProductIds = (queryParameters) =>
	queryParameters?.product_id
		? [].concat(queryParameters?.product_id).map(Number)
		: [];

const Jamfor = ({ location }) => {
	const { t } = useTranslation('comparison');
	const [showProductList, setShowProductList] = useState(false);
	const { queryParameters } = useQueryParameters();
	const productIds = getProductIds(queryParameters);

	const siteMetadata = useSiteMetadata();

	const { items: allProducts } = useProduct({
		exclude_groups: [...siteMetadata.config.business_tests_groups],
		has_markers: true,
		include_product_types: ['checkup'],
	});

	const toggleProductList = () => setShowProductList(!showProductList);

	const [isMobile, setIsMobile] = useState(false);
	const { width } = useWindowSize();

	useEffect(() => {
		setIsMobile(width < 768);
	}, [width]);

	return (
		<Framework
			description={t('meta_description')}
			location={location}
			tags={t('meta_keywords')}
			title={t('title')}
		>
			<section id="content">
				<div className="container pt-5">
					<PageHeader pageVariant="nested_page" resetSpacing title={t('t1')} />

					{isMobile && productIds.length > 0 && (
						<div className="d-block d-md-none mt-4">
							<button
								className="btn btn-primary btn-lg w-100 rounded-pill"
								onClick={toggleProductList}
							>
								<Plus /> {t('t6')}
							</button>
						</div>
					)}
				</div>

				<div className="container mt-4 px-0 px-lg-3">
					<div className="row">
						{productIds.length > 0 ? (
							<>
								<div className="col-md-9 px-0">
									<ProductsTable
										allProducts={allProducts}
										toggleProductList={toggleProductList}
									/>
								</div>

								{!isMobile && productIds.length > 0 && (
									<div className="col-md-3">
										<div className="compare-table-product-list">
											<SectionHeader
												headingType="h3"
												hideButton
												title={t('t6')}
											/>
											<div>
												<ProductSelectionList allProducts={allProducts} />
											</div>
										</div>
									</div>
								)}
							</>
						) : (
							<div className="col-lg-8 px-3">
								<NoProductsSelected
									allProducts={allProducts}
									toggleProductList={toggleProductList}
								/>
							</div>
						)}
					</div>
				</div>
			</section>

			{isMobile && showProductList && (
				<div
					aria-labelledby="offcanvasLabel"
					className={`offcanvas offcanvas-bottom ${showProductList ? 'show' : ''}`}
					id="productOffcanvas"
					style={{ height: '50vh' }}
					tabIndex="-1"
				>
					<div className="offcanvas-header">
						<h5 id="offcanvasLabel">{t('t6')}</h5>
						<button
							className="btn-close"
							onClick={toggleProductList}
							type="button"
						></button>
					</div>
					<div className="offcanvas-body">
						<ProductSelectionList
							allProducts={allProducts}
							onHide={toggleProductList}
							show={showProductList}
						/>
					</div>
				</div>
			)}
		</Framework>
	);
};

const ProductSelectionList = ({ allProducts, onHide }) => {
	const { t } = useTranslation('comparison');
	const { queryParameters, setQueryParameter } = useQueryParameters();
	const productIds = getProductIds(queryParameters);

	const availableProducts = allProducts.filter(
		(product) => !productIds.includes(product.product_id),
	);

	const addProduct = (productId) => {
		setQueryParameter('product_id', [...productIds, productId].join(','));
		onHide?.();
	};

	return (
		<div className="compare-table-product-list">
			<div>
				{availableProducts.length > 0 ? (
					availableProducts.map((product) => (
						<ProductSelectionListItem
							addProduct={addProduct}
							key={product.product_id}
							product={product}
						/>
					))
				) : (
					<p className="text-center text-muted">{t('t8')}</p>
				)}
			</div>
		</div>
	);
};

const ProductSelectionListItem = ({ addProduct, product }) => {
	const product_image = product?.fields?.files_header_url_src;
	return (
		<div className="compare-table-product-list-item">
			<div
				className=" w-100 d-flex align-items-center justify-content-between"
				onClick={() => addProduct(product.product_id)}
				type="button"
			>
				<div className="d-flex align-items-center">
					{product_image && (
						<>
							<GatsbyImage
								alt={product.product_variant.title}
								className="img-fluid thumb-sm me-2 bg-light"
								draggable="false"
								image={getImage(product_image)}
							/>
						</>
					)}
					{!product_image && (
						<MarkerThumb
							className="me-2"
							groups={product.groups}
							markers={product.markers}
							product_type={product.type}
							size="sm"
						/>
					)}

					<div>
						<p className="mb-0 fw-700 sm">{product.product_variant.title}</p>
						<PriceItem
							className={'fw-500 mb-0 xs text-number me-2'}
							currency={product.product_variant.currency?.code}
							discount={product.product_variant.price_discount}
							price={product.product_variant.price}
							price_
						/>
					</div>
				</div>
				<Plus className="compare-table-product-list-item-icon" />
			</div>
		</div>
	);
};
const ProductRow = ({ customOnChange, product }) => {
	const { t } = useTranslation('comparison');
	const product_image = product?.fields?.files_header_url_src;

	return (
		<th className="align-middle">
			<div className="d-flex justify-content-end w-100">
				<button
					className="btn compare-table-close-btn"
					onClick={() => customOnChange(product, { value: null })}
				>
					<XLg />
				</button>
			</div>
			<GroupLink
				className="text-decoration-none"
				groups={product.groups}
				page="tester"
				product_id={product.product_id}
			>
				{product_image && (
					<>
						<GatsbyImage
							alt={product.product_variant.title}
							className="img-fluid thumb-lg me-2 bg-light "
							draggable="false"
							image={getImage(product_image)}
						/>
					</>
				)}
				{!product_image && (
					<MarkerThumb
						className="me-2"
						groups={product.groups}
						markers={product.markers}
						product_type={product.type}
						size="lg"
					/>
				)}

				<div className="text-dark mb-0 card-title text-break white-space-break mt-2 text-decoration-none">
					{product.product_variant.title}
				</div>

				<div className="text-dark list-subtitle biomarkers border-dark text-decoration-none">
					{product.markers.length} {t('t5')}
				</div>
				<div className="d-block mt-2 price">
					<PriceItem
						className={'fw-500 mb-0 text-number me-2 text-dark'}
						currency={product.product_variant.currency?.code}
						price={product.product_variant.price}
						price_discount={product.product_variant.price_discount}
					/>
				</div>
			</GroupLink>
		</th>
	);
};

const ProductHeader = ({
	allProducts,
	customOnChange,
	productIds,
	productOptions,
}) => {
	const products = allProducts.filter((product) =>
		[...productIds].includes(product.product_id),
	);

	return (
		<thead>
			<tr>
				<th className="align-middle" />
				{products.map((product) => (
					<ProductRow
						customOnChange={customOnChange}
						key={product.product_id || 'empty'}
						product={product}
						productOptions={productOptions}
					/>
				))}
			</tr>
		</thead>
	);
};

const ProductBody = ({ groups, groupsFiltered, markers, products }) => {
	const getGroupImage = (group_id) => {
		let out = null;
		const match = groups.find(
			(item) => item.group_id == group_id && item?.fields?.files_icon_url_src,
		);
		if (match) out = match?.fields?.files_icon_url_src;
		return out;
	};

	const groupMarkers = (groupId) => {
		return markers.filter((marker_item) =>
			marker_item.groups.includes(Number(groupId)),
		);
	};

	return (
		<tbody>
			{Object.entries(groupsFiltered.items).map(([group_id, group]) => (
				<Fragment key={group_id}>
					<GroupRow
						group={group}
						image={getGroupImage(group.group_id)}
						markers={markers}
						products={products}
					/>

					<MarkerRow
						groupMarkers={groupMarkers(group.group_id)}
						groups={groups}
						products={products}
					/>
				</Fragment>
			))}
		</tbody>
	);
};

const GroupRow = ({ group, image, markers, products }) => {
	const countMarkersForProductAndGroup = (product, groupId) => {
		const filteredMarkers = markers.filter((marker_item) =>
			!marker_item.groups?.length
				? groupId === -1
				: marker_item.groups.includes(Number(groupId)) &&
					product.markers.includes(marker_item.marker_id),
		);

		return filteredMarkers.length;
	};
	return (
		<tr>
			<td className="bg-gray-100 align-middle ">
				<div className="d-flex gap-3 align-items-center text-nowrap overflow-hidden text-truncate">
					{image && (
						<GatsbyImage
							alt={`${group.title} icon`}
							className="icon-md img-fluid"
							image={getImage(image)}
						/>
					)}
					{group.title}
				</div>
			</td>
			{products.map((product) => (
				<td
					className="bg-gray-100"
					key={`${product.product_id}-${group.group_id}`}
				>
					<div>{countMarkersForProductAndGroup(product, group.group_id)}</div>
				</td>
			))}
		</tr>
	);
};

const MarkerRow = ({ groupMarkers, groups, products }) => {
	const { t } = useTranslation('comparison');
	const isMarkerInProduct = (product, marker) => {
		return product.markers.includes(marker.marker_id);
	};

	return groupMarkers.map((marker) => {
		const marker_group_id = groups.find(
			(item) =>
				marker?.groups?.includes(item.group_id) && item.type == 'marker',
		)?.group_id;

		return (
			<>
				<tr key={marker.marker_id}>
					<td>
						<button
							className="btn btn-link p-0 text-dark compare-table"
							data-bs-target={`#offcanvas-marker-${marker.marker_variant.marker_variant_id}`}
							data-bs-toggle="offcanvas"
							type="button"
						>
							{marker.marker_variant.title}
						</button>
					</td>
					{products.map((product) => (
						<td key={`${product.product_id}-${marker.marker_id}`}>
							{isMarkerInProduct(product, marker) ? (
								<CheckLg className="text-success fs-5" />
							) : (
								<XLg className="text-danger fs-5" />
							)}
						</td>
					))}
				</tr>
				<div
					aria-labelledby={`offcanvasMarkerLabel-${marker.marker_variant.marker_variant_id}`}
					className="offcanvas offcanvas-end"
					data-bs-scroll="true"
					id={`offcanvas-marker-${marker.marker_variant.marker_variant_id}`}
				>
					<div className="offcanvas-header">
						<h5
							id={`offcanvasMarkerLabel-${marker.marker_variant.marker_variant_id}`}
						>
							{marker.marker_variant.title}
						</h5>
						<button
							aria-label="Close"
							className="btn-close"
							data-bs-dismiss="offcanvas"
							type="button"
						></button>
					</div>
					<div className="offcanvas-body">
						<Trans defaults={marker.marker_variant.excerpt} t={t} />
						<ReadMoreLink
							groups={[marker_group_id]}
							label={t('t9')}
							marker_id={marker.marker_id}
							page={marker.type == 'allergen' ? 'allergener' : 'markorer'}
						/>
					</div>
				</div>
			</>
		);
	});
};

const NoProductsSelected = ({ allProducts }) => {
	const { t } = useTranslation('comparison');
	const { queryParameters, setQueryParameter } = useQueryParameters();
	const productIds = getProductIds(queryParameters);

	const availableProducts = allProducts.filter(
		(product) => !productIds.includes(product.product_id),
	);

	const addProduct = (productId) => {
		setQueryParameter('product_id', [...productIds, productId].join(','));
	};

	return (
		<div className="">
			<div className="">
				<SectionHeader hideButton size="sm" title={t('t7')} />
			</div>

			<div className="">
				{availableProducts.length > 0 ? (
					availableProducts.map((product) => (
						<ProductSelectionListItem
							addProduct={addProduct}
							key={product.product_id}
							product={product}
						/>
					))
				) : (
					<p className="text-muted">{t('t8')}</p>
				)}
			</div>
		</div>
	);
};

const ProductsTable = ({ allProducts }) => {
	const { queryParameters, setQueryParameter } = useQueryParameters();
	const { language, t } = useTranslation('comparison');

	const productIds = getProductIds(queryParameters);
	const products = allProducts.filter((product) =>
		[...productIds].includes(product.product_id),
	);

	const markerIds = new Set(
		products.flatMap((product) => product?.markers ?? []),
	);
	const { items: markers } = useMarker({
		include_markers: Array.from(markerIds),
	});

	const { items: groups } = useGroup({
		include_types: ['marker', 'marker_domain'],
	});
	const groups_filtered = getGroupsByMarker({
		fallback_group: { group_id: -1, title: t('t3') },
		groups: groups.filter((group) => group.type == 'marker_domain'),
		language,
		markers,
	});

	const customOnChange = (selectedProduct, { value }) => {
		let updatedProductIds = [];

		if (selectedProduct) {
			updatedProductIds = products.map((product) =>
				product.product_id === selectedProduct?.product_id
					? value
					: product.product_id,
			);
		} else {
			updatedProductIds = [...productIds, value];
		}

		setQueryParameter(
			'product_id',
			updatedProductIds.filter(Boolean).join(','),
		);
	};

	return (
		<div className="table-responsive compare-table">
			<table className="table align-middle table-bordered table-hover">
				<ProductHeader
					allProducts={allProducts}
					customOnChange={customOnChange}
					productIds={productIds}
				/>

				<ProductBody
					groups={groups}
					groupsFiltered={groups_filtered}
					markers={markers}
					products={products}
				/>
			</table>
		</div>
	);
};

Jamfor.propTypes = {
	location: PropTypes.object.isRequired,
};

ProductBody.propTypes = {
	groups: PropTypes.array.isRequired,
	groupsFiltered: PropTypes.object.isRequired,
	markers: PropTypes.array.isRequired,
	products: PropTypes.array.isRequired,
};

ProductSelectionList.propTypes = {
	allProducts: PropTypes.array,
	isMobile: PropTypes.bool,
	onHide: PropTypes.func,
	show: PropTypes.bool,
};

ProductSelectionListItem.propTypes = {
	addProduct: PropTypes.func,
	product: PropTypes.object,
};

ProductRow.propTypes = {
	customOnChange: PropTypes.func.isRequired,
	product: PropTypes.object.isRequired,
	productOptions: PropTypes.array.isRequired,
};

ProductHeader.propTypes = {
	allProducts: PropTypes.array.isRequired,
	customOnChange: PropTypes.func.isRequired,
	productIds: PropTypes.array.isRequired,
	productOptions: PropTypes.array.isRequired,
};

GroupRow.propTypes = {
	group: PropTypes.object.isRequired,
	image: PropTypes.object.isRequired,
	markers: PropTypes.array.isRequired,
	products: PropTypes.array.isRequired,
};

MarkerRow.propTypes = {
	groupMarkers: PropTypes.array.isRequired,
	groups: PropTypes.array.isRequired,
	products: PropTypes.array.isRequired,
};

NoProductsSelected.propTypes = {
	allProducts: PropTypes.array.isRequired,
	customOnChange: PropTypes.func.isRequired,
	productOptions: PropTypes.array.isRequired,
};

ProductsTable.propTypes = {
	allProducts: PropTypes.array.isRequired,
};

export default Jamfor;

export const query = graphql`
	query {
		locales: allLocale {
			...translationFields
		}
	}
`;
